import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useScope } from '../lib/scope';
import { formatShort } from '../lib/format';
import { iso, localDateTime, plusMinutes, TEAM_DIVISION_LABELS, type Season, type Shift, type Team } from '../lib/sports';
import { TEAM_DIVISION, type TeamDivision } from '@ukinfo/shared';
import { PageHeader } from '../components/PageHeader';
import { LinkButton, TestBadge } from '../components/training/common';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  SectionTitle,
  Select,
  cn,
  useToast,
} from '../components/ui';

type Unit = { id: string; name: string; path: string[] };
type TeamCategory = 'FIRST' | 'RESERVE' | 'YOUTH' | 'OTHER';
/** Verbandsmannschaft, zu der in UK Info noch keine Mannschaft passt. */
interface TeamSuggestion {
  id: string;
  label: string;
  competition: string | null;
  matchCount: number;
  firstMatchAt: string | null;
  lastMatchAt: string | null;
  organisationUnitId: string | null;
  organisationUnitName: string | null;
  suggested: { name: string; category: TeamCategory; ageClass: number | null };
}

type CopyOption = { team_id: string; name: string; suggested_name: string; category: TeamCategory; age_class: number | null; player_count: number };
type YouthPlanning = { groups: { ageClass: number; suggestedName: string; birthYear: number; playerCount: number; currentTeams: string[] }[]; knownBirthDates: number; missingBirthDates: number };

const aktuellesJahr = new Date().getFullYear();

function followingSeason(latest?: Season): { name: string; start: string; end: string } {
  if (!latest) return { name: `${aktuellesJahr}/${String(aktuellesJahr + 1).slice(-2)}`, start: `${aktuellesJahr}-07-01`, end: `${aktuellesJahr + 1}-06-30` };
  const startYear = Number(latest.start_date.slice(0, 4)) + 1;
  const endYear = Number(latest.end_date.slice(0, 4)) + 1;
  return {
    name: `${startYear}/${String(endYear).slice(-2)}`,
    start: `${startYear}${latest.start_date.slice(4)}`,
    end: `${endYear}${latest.end_date.slice(4)}`,
  };
}

/**
 * Einstieg in den Sportbereich: welche Mannschaften gibt es?
 *
 * Alles Mannschaftsbezogene - Trainings, Spiele, Kader, Auswertungen - liegt
 * eine Ebene tiefer. Vorher stand es gleichzeitig auf einer Seite, was bei
 * mehreren Mannschaften nur ueber Auswahlfelder auseinanderzuhalten war.
 */
export function SportsPage(): JSX.Element {
  const scope = useScope();
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();

  const [seasonId, setSeasonId] = useState('');
  const [teamDialog, setTeamDialog] = useState(false);
  const [seasonDialog, setSeasonDialog] = useState(false);
  const [copyDialog, setCopyDialog] = useState(false);
  const [shiftDialog, setShiftDialog] = useState(false);

  const [teamName, setTeamName] = useState('');
  const [teamUnitId, setTeamUnitId] = useState(scope.unitId ?? '');
  const [teamCategory, setTeamCategory] = useState<TeamCategory>('OTHER');
  // Herren/Damen folgt der Art, bis jemand es selbst waehlt.
  const [teamDivision, setTeamDivision] = useState<TeamDivision>('MEN');
  const [divisionGewaehlt, setDivisionGewaehlt] = useState(false);
  const [divisionFilter, setDivisionFilter] = useState<TeamDivision | 'ALLE'>('ALLE');
  const [teamAgeClass, setTeamAgeClass] = useState<number | null>(null);
  const [seasonName, setSeasonName] = useState(`${aktuellesJahr}/${String(aktuellesJahr + 1).slice(-2)}`);
  const [seasonStart, setSeasonStart] = useState(`${aktuellesJahr}-07-01`);
  const [seasonEnd, setSeasonEnd] = useState(`${aktuellesJahr + 1}-06-30`);
  const [copySeniors, setCopySeniors] = useState(true);
  const [copySourceSeasonId, setCopySourceSeasonId] = useState('');
  const [copyDrafts, setCopyDrafts] = useState<Record<string, { selected: boolean; name: string; copyRoster: boolean }>>({});
  const [shiftTitle, setShiftTitle] = useState('Helferdienst');
  const [shiftStart, setShiftStart] = useState(localDateTime(2));
  const [shiftEnd, setShiftEnd] = useState(() => plusMinutes(localDateTime(2), 120));
  // Offene Frage aus dem Verbandsfeed, die dieser Dialog gerade beantwortet.
  const [offeneFrage, setOffeneFrage] = useState<TeamSuggestion | null>(null);
  const [shiftCapacity, setShiftCapacity] = useState('1');

  const seasons = useQuery({
    queryKey: ['sports-seasons'],
    queryFn: () => api.get<Season[]>('/api/sports/seasons'),
  });
  useEffect(() => {
    if (seasons.data?.length && !seasonId) setSeasonId(seasons.data[0]!.id);
  }, [seasons.data, seasonId]);

  const selectedSeason = seasons.data?.find((season) => season.id === seasonId);
  const previousSeasons = useMemo(
    () => (seasons.data ?? []).filter((season) => selectedSeason && season.end_date < selectedSeason.start_date),
    [seasons.data, selectedSeason],
  );
  const previousSeason = previousSeasons[0];
  useEffect(() => {
    if (previousSeason && !previousSeasons.some((season) => season.id === copySourceSeasonId)) setCopySourceSeasonId(previousSeason.id);
  }, [previousSeason, previousSeasons, copySourceSeasonId]);

  const teams = useQuery({
    queryKey: ['sports-teams', scope.unitId, seasonId],
    queryFn: () =>
      api.get<Team[]>('/api/sports/teams', {
        organisationUnitId: scope.unitId,
        includeChildren: true,
        ...(seasonId ? { seasonId } : {}),
      }),
    enabled: Boolean(seasonId),
  });
  const copyOptions = useQuery({
    queryKey: ['sports-season-copy-options', seasonId, copySourceSeasonId],
    queryFn: () => api.get<CopyOption[]>(`/api/sports/seasons/${seasonId}/copy-options`, { sourceSeasonId: copySourceSeasonId }),
    enabled: copyDialog && Boolean(seasonId && copySourceSeasonId),
  });
  const youthPlanning = useQuery({
    queryKey: ['sports-youth-planning', seasonId, previousSeason?.id],
    queryFn: () => api.get<YouthPlanning>(`/api/sports/seasons/${seasonId}/youth-planning`, { sourceSeasonId: previousSeason!.id }),
    enabled: Boolean(seasonId && previousSeason?.id),
  });
  useEffect(() => {
    if (!copyOptions.data) return;
    setCopyDrafts(Object.fromEntries(copyOptions.data.map((option) => [option.team_id, {
      selected: true, name: option.suggested_name, copyRoster: true,
    }])));
  }, [copyOptions.data]);
  const units = useQuery({
    queryKey: ['organisation', 'units'],
    queryFn: () => api.get<Unit[]>('/api/organisation/units'),
  });
  const shifts = useQuery({
    queryKey: ['helper-shifts', scope.unitId],
    queryFn: () =>
      api.get<Shift[]>('/api/helpers/shifts', {
        organisationUnitId: scope.unitId,
        includeChildren: true,
      }),
  });

  const fehler = (fallback: string) => (error: unknown) =>
    toast.show(error instanceof ApiError ? error.message : fallback, 'error');

  const vorhandeneBereiche = TEAM_DIVISION.filter((wert) => (teams.data ?? []).some((team) => team.division === wert));
  const gefilterteTeams = (teams.data ?? []).filter((team) => divisionFilter === 'ALLE' || team.division === divisionFilter);

  const createSeason = useMutation({
    mutationFn: () =>
      api.post<Season>('/api/sports/seasons', {
        name: seasonName,
        startDate: seasonStart,
        endDate: seasonEnd,
        copySeniorFromSeasonId: copySeniors ? seasons.data?.[0]?.id : undefined,
      }),
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['sports-seasons'] });
      await qc.invalidateQueries({ queryKey: ['sports-teams'] });
      setSeasonId(created.id);
      setSeasonDialog(false);
      toast.show(copySeniors && seasons.data?.length ? 'Saison mit Erwachsenenmannschaften angelegt' : 'Saison angelegt');
    },
    onError: fehler('Saison konnte nicht angelegt werden'),
  });

  const copyTeams = useMutation({
    mutationFn: () => api.post(`/api/sports/seasons/${seasonId}/teams/copy`, {
      sourceSeasonId: copySourceSeasonId,
      teams: Object.entries(copyDrafts).filter(([, draft]) => draft.selected).map(([teamId, draft]) => ({ teamId, name: draft.name, copyRoster: draft.copyRoster })),
    }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sports-teams'] });
      await qc.invalidateQueries({ queryKey: ['sports-youth-planning'] });
      setCopyDialog(false);
      toast.show('Mannschaften aus Vorsaison übernommen');
    },
    onError: fehler('Mannschaften konnten nicht übernommen werden'),
  });

  const createShift = useMutation({
    mutationFn: () =>
      api.post('/api/helpers/shifts', {
        organisationUnitId: scope.unitId ?? units.data?.[0]?.id,
        title: shiftTitle,
        description: '',
        startAt: iso(shiftStart),
        endAt: iso(shiftEnd),
        capacity: Number(shiftCapacity),
        calendarEventId: null,
        taskId: null,
        status: 'OPEN',
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['helper-shifts'] });
      setShiftDialog(false);
      toast.show('Helferdienst angelegt');
    },
    onError: fehler('Helferdienst konnte nicht angelegt werden'),
  });

  const signupShift = useMutation({
    mutationFn: (input: { id: string; status: 'CONFIRMED' | 'DECLINED' }) =>
      api.put(`/api/helpers/shifts/${input.id}/signup`, { status: input.status }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['helper-shifts'] });
      toast.show('Helferdienst-Rückmeldung gespeichert');
    },
    onError: fehler('Helferdienst-Rückmeldung konnte nicht gespeichert werden'),
  });

  const mayCreate = can('persons', 'create');

  const suggestions = useQuery({
    queryKey: ['sports-team-suggestions'],
    queryFn: () => api.get<TeamSuggestion[]>('/api/sports/team-suggestions'),
    enabled: mayCreate,
  });

  // Welche vorhandene Mannschaft je offener Frage gewählt ist.
  const [zuordnung, setZuordnung] = useState<Record<string, string>>({});

  const zuordnen = useMutation({
    mutationFn: (input: { id: string; teamId: string }) =>
      api.post<{ matches: number }>(`/api/sports/team-suggestions/${input.id}/team`, { teamId: input.teamId }),
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['sports-team-suggestions'] });
      await qc.invalidateQueries({ queryKey: ['matches'] });
      await qc.invalidateQueries({ queryKey: ['calendar'] });
      toast.show(`Zugeordnet, ${result.matches} ${result.matches === 1 ? 'Termin' : 'Termine'} übernommen`);
    },
    onError: fehler('Zuordnung fehlgeschlagen'),
  });

  const verwerfen = useMutation({
    mutationFn: (id: string) => api.post(`/api/sports/team-suggestions/${id}/dismiss`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sports-team-suggestions'] });
      toast.show('Nicht mehr vorgeschlagen');
    },
    onError: fehler('Vorschlag konnte nicht weggelegt werden'),
  });

  const frageBeantworten = (frage: TeamSuggestion): void => {
    setOffeneFrage(frage);
    setTeamName(frage.suggested.name);
    setTeamUnitId(frage.organisationUnitId ?? scope.unitId ?? verfuegbareBereiche[0]?.id ?? '');
    setTeamCategory(frage.suggested.category);
    setTeamAgeClass(frage.suggested.ageClass);
    setDivisionGewaehlt(false);
    setTeamDialog(true);
  };


  const createTeam = useMutation({
    mutationFn: async () => {
      const team = await api.post<Team>('/api/sports/teams', { name: teamName, organisationUnitId: teamUnitId, seasonId, category: teamCategory, ageClass: teamCategory === 'YOUTH' ? teamAgeClass : null, division: teamDivision });
      // Kommt die Mannschaft aus einer offenen Frage des Verbandsfeeds,
      // wandern ihre schon uebernommenen Spiele gleich mit.
      if (!offeneFrage) return { team, matches: 0 };
      const result = await api.post<{ matches: number }>(`/api/sports/team-suggestions/${offeneFrage.id}/team`, { teamId: team.id });
      return { team, matches: result.matches };
    },
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['sports-teams'] });
      await qc.invalidateQueries({ queryKey: ['sports-team-suggestions'] });
      setTeamName('');
      setDivisionGewaehlt(false);
      setTeamDialog(false);
      setOffeneFrage(null);
      toast.show(result.matches > 0 ? `Mannschaft angelegt, ${result.matches} Spiele übernommen` : 'Mannschaft angelegt');
    },
    onError: fehler('Mannschaft konnte nicht angelegt werden'),
  });

  const mayHelpers = can('tasks', 'create');
  const verfuegbareBereiche = units.data ?? [];
  const openSeasonDialog = () => {
    const next = followingSeason(seasons.data?.[0]);
    setSeasonName(next.name);
    setSeasonStart(next.start);
    setSeasonEnd(next.end);
    setCopySeniors(Boolean(seasons.data?.length));
    setSeasonDialog(true);
  };
  const selectedCopyDrafts = Object.values(copyDrafts).filter((draft) => draft.selected);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sport"
        description="Mannschaften, Kader und Trainingsverwaltung"
        actions={
          <div className="flex flex-wrap gap-2">
            {mayCreate && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setTeamUnitId(scope.unitId ?? verfuegbareBereiche[0]?.id ?? '');
                    setTeamCategory('OTHER');
                    setTeamAgeClass(null);
                    setTeamDialog(true);
                  }}
                >
                  Mannschaft anlegen
                </Button>
                <Button variant="secondary" onClick={openSeasonDialog}>
                  Saison anlegen
                </Button>
                {previousSeason && (
                  <Button variant="secondary" onClick={() => setCopyDialog(true)}>
                    Aus Vorsaison übernehmen …
                  </Button>
                )}
              </>
            )}
            {mayHelpers && (
              <Button variant="secondary" onClick={() => setShiftDialog(true)}>
                Helferdienst anlegen
              </Button>
            )}
          </div>
        }
      />

      {(teams.isLoading || seasons.isLoading) && <LoadingBlock />}

      {(suggestions.data?.length ?? 0) > 0 && (
        <Card>
          <CardBody className="space-y-3">
            <SectionTitle
              title="Termine ohne Mannschaft"
              subtitle="Aus dem Verbandskalender – die Termine sind übernommen, die Mannschaft fehlt noch"
            />
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
              {suggestions.data?.map((frage) => (
                <li key={frage.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-900">{frage.label}</span>
                    <span className="block text-xs text-slate-500">
                      {frage.matchCount} {frage.matchCount === 1 ? 'Termin' : 'Termine'}
                      {frage.firstMatchAt && ` · ${formatShort(frage.firstMatchAt)} bis ${formatShort(frage.lastMatchAt ?? frage.firstMatchAt)}`}
                      {frage.competition && ` · ${frage.competition}`}
                      {frage.organisationUnitName && ` · vorläufig unter ${frage.organisationUnitName}`}
                    </span>
                  </span>
                  <span className="flex flex-wrap items-center gap-2">
                    {/* Heißt die Mannschaft hier anders als beim Verband
                        ("1b" statt "Reserve"), wird sie einfach zugeordnet. */}
                    <Select
                      aria-label={`Vorhandene Mannschaft für ${frage.label}`}
                      className="h-9 w-auto py-0 text-sm"
                      value={zuordnung[frage.id] ?? ''}
                      onChange={(event) => setZuordnung((vorher) => ({ ...vorher, [frage.id]: event.target.value }))}
                    >
                      <option value="">vorhandene Mannschaft …</option>
                      {(teams.data ?? []).map((team) => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </Select>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!zuordnung[frage.id]}
                      loading={zuordnen.isPending}
                      onClick={() => zuordnen.mutate({ id: frage.id, teamId: zuordnung[frage.id] as string })}
                    >
                      Zuordnen
                    </Button>
                    <Button size="sm" onClick={() => frageBeantworten(frage)}>Neu anlegen</Button>
                    <Button size="sm" variant="ghost" loading={verwerfen.isPending} onClick={() => verwerfen.mutate(frage.id)}>
                      Nicht anlegen
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500">
              Heißt eine Mannschaft hier anders als beim Verband – etwa „1b“ statt „Reserve“ oder „UK KM“ statt
              „Kampfmannschaft“ –, wird sie mit „Zuordnen“ verbunden; ihre Termine wandern dann dorthin. „Neu anlegen“
              ist für Mannschaften, die es hier noch nicht gibt. Ohne Mannschaft bleiben die Termine im Kalender des
              Vereins; erst mit der Mannschaft gibt es Kader, Rückmeldungen und Anwesenheit dazu.
            </p>
          </CardBody>
        </Card>
      )}

      {(seasons.data?.length ?? 0) > 1 && (
        <Card>
          <CardBody>
            <Field label="Saison" hint="Gilt für Kadergröße und Termine der Mannschaften">
              {(id) => (
                <Select id={id} value={seasonId} onChange={(e) => setSeasonId(e.target.value)}>
                  {seasons.data?.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </CardBody>
        </Card>
      )}

      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-slate-900">Mannschaften</h2>
          {vorhandeneBereiche.length > 1 && (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Nach Damen/Herren filtern">
              {(['ALLE', ...vorhandeneBereiche] as const).map((wert) => (
                <button
                  key={wert}
                  type="button"
                  aria-pressed={divisionFilter === wert}
                  onClick={() => setDivisionFilter(wert)}
                  className={cn(
                    'rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
                    divisionFilter === wert ? 'bg-slate-800 text-white ring-slate-800' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50',
                  )}
                >
                  {wert === 'ALLE' ? 'Alle' : TEAM_DIVISION_LABELS[wert]}
                </button>
              ))}
            </div>
          )}
        </div>
        {gefilterteTeams.length ? (
          <ul className="grid gap-3 sm:grid-cols-2">
            {gefilterteTeams.map((team) => (
              <li key={team.id}>
                <Link
                  to={`/sport/${team.id}${seasonId ? `?saison=${seasonId}` : ''}`}
                  className="block h-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block font-semibold text-slate-900">
                        {team.name}
                        {team.division && team.division !== 'MIXED' && (
                          <span className="ml-2 align-middle text-xs font-medium text-slate-500">{TEAM_DIVISION_LABELS[team.division]}</span>
                        )}
                      </span>
                      {/* Oft heisst der Bereich wie die Mannschaft - dann sagt
                          die Wiederholung nichts. */}
                      {team.unit_name && team.unit_name !== team.name && (
                        <span className="block text-xs text-slate-500">{team.unit_name}</span>
                      )}
                    </span>
                    {team.playerCount !== undefined && (
                      <Badge tone="neutral">
                        {team.playerCount} {team.playerCount === 1 ? 'Person' : 'Personen'}
                        {team.occasionalCount ? ` + ${team.occasionalCount} Aushilfe${team.occasionalCount === 1 ? '' : 'n'}` : ''}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-3 text-sm text-slate-600">
                    {team.nextSession ? (
                      <>
                        Als Nächstes: {team.nextSession.title} ·{' '}
                        {formatShort(team.nextSession.startAt)}
                      </>
                    ) : (
                      'Kein Termin geplant'
                    )}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="Keine Mannschaften"
            description={
              mayCreate
                ? 'Lege die erste Mannschaft für den gewählten Bereich an.'
                : 'Mannschaften werden im Vereinsbereich angelegt.'
            }
          />
        )}
      </section>

      {can('sports_training_plans', 'read') && (
        <Card>
          <CardBody className="space-y-3">
            <SectionTitle
              title="Trainingsplanung"
              subtitle="Übungen mit Skizze, Vorlagen für Trainingseinheiten und Formationen"
              action={<TestBadge />}
            />
            <div className="flex flex-wrap gap-2">
              <LinkButton to="/sport/uebungen">Übungen</LinkButton>
              <LinkButton to="/sport/einheiten">Einheiten</LinkButton>
              <LinkButton to="/sport/formationen">Formationen</LinkButton>
            </div>
          </CardBody>
        </Card>
      )}

      {selectedSeason && (
        <Card>
          <CardBody>
            <SectionTitle
              title="Nachwuchsmannschaften planen"
              subtitle={previousSeason
                ? `Rechnerische Jahrgänge für ${selectedSeason.name}`
                : `Vorbereitung der nächsten Saison ${followingSeason(selectedSeason).name}`}
            />
            {!previousSeason && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Lege die nächste Saison an. Danach zeigt die Planung anhand der Nachwuchskader aus {selectedSeason.name} und der Geburtsdaten, welche U-Mannschaften möglich sind.
                </p>
                {mayCreate && <Button variant="secondary" onClick={openSeasonDialog}>Saison {followingSeason(selectedSeason).name} anlegen und Nachwuchs planen</Button>}
              </div>
            )}
            {previousSeason && youthPlanning.isLoading && <LoadingBlock />}
            {previousSeason && youthPlanning.data && (
              <>
                <p className="mb-3 text-sm text-slate-600">
                  Grundlage sind die Kader der Vorsaison und hinterlegte Geburtsdaten. Die Vorschläge ersetzen keine Prüfung der Spielberechtigung beim Landesverband.
                </p>
                {youthPlanning.data.groups.length > 0 ? (
                  <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {youthPlanning.data.groups.map((group) => (
                      <li key={group.ageClass} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{group.suggestedName}</span>
                          <Badge tone="neutral">{group.playerCount} {group.playerCount === 1 ? 'Person' : 'Personen'}</Badge>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">Jahrgang {group.birthYear}{group.currentTeams.length ? ` · bisher ${group.currentTeams.join(', ')}` : ''}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyState title="Noch keine Jahrgangsvorschläge" description="In den Kadern der Vorsaison fehlen passende Geburtsdaten." />
                )}
                {youthPlanning.data.missingBirthDates > 0 && (
                  <p className="mt-3 text-sm text-amber-700">Bei {youthPlanning.data.missingBirthDates} Kaderpersonen fehlt das Geburtsdatum.</p>
                )}
                {mayCreate && (
                  <Button className="mt-3" variant="secondary" onClick={() => setCopyDialog(true)}>
                    Nachwuchsmannschaften aus {previousSeason.name} auswählen und übernehmen
                  </Button>
                )}
              </>
            )}
          </CardBody>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardBody>
            <SectionTitle title="Saisonen" subtitle="Zeiträume für Kader und Termine" />
            {seasons.data?.length ? (
              <ul className="space-y-2">
                {seasons.data.map((s) => (
                  <li key={s.id} className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className="font-medium">{s.name}</p>
                    <p className="text-xs text-slate-500">
                      {s.start_date} bis {s.end_date}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="Keine Saisonen" description="Saisonen werden im Vereinsbereich angelegt." />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <SectionTitle title="Helferdienste" subtitle="Gilt für den gewählten Bereich" />
            {shifts.data?.length ? (
              <ul className="space-y-2">
                {shifts.data.map((s) => (
                  <li
                    key={s.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm"
                  >
                    <span>
                      <span className="block font-medium">{s.title}</span>
                      <span className="text-xs text-slate-500">{formatShort(s.start_at)}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge tone={s.myStatus === 'CONFIRMED' ? 'success' : 'neutral'}>
                        {s.confirmed}/{s.capacity}
                      </Badge>
                      {s.myStatus === 'CONFIRMED' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={signupShift.isPending}
                          onClick={() => signupShift.mutate({ id: s.id, status: 'DECLINED' })}
                        >
                          Absagen
                        </Button>
                      ) : (
                        s.status === 'OPEN' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={signupShift.isPending}
                            onClick={() => signupShift.mutate({ id: s.id, status: 'CONFIRMED' })}
                          >
                            Zusagen
                          </Button>
                        )
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title="Keine Helferdienste"
                description="Für den gewählten Bereich sind keine Dienste eingetragen."
              />
            )}
          </CardBody>
        </Card>
      </div>

      <Dialog
        open={teamDialog}
        onClose={() => { setTeamDialog(false); setOffeneFrage(null); }}
        title={offeneFrage ? `Mannschaft "${offeneFrage.label}" anlegen` : 'Mannschaft anlegen'}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setTeamDialog(false); setOffeneFrage(null); }}>
              Abbrechen
            </Button>
            <Button
              loading={createTeam.isPending}
              disabled={!teamName.trim() || !teamUnitId || !seasonId || (teamCategory === 'YOUTH' && teamAgeClass === null)}
              onClick={() => createTeam.mutate()}
            >
              Anlegen
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name" required>
            {(id) => (
              <Input
                id={id}
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="z. B. U16"
                autoFocus
              />
            )}
          </Field>
          <Field label="Bereich" required>
            {(id) => (
              <Select id={id} value={teamUnitId} onChange={(e) => setTeamUnitId(e.target.value)}>
                <option value="">Bereich auswählen …</option>
                {verfuegbareBereiche.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.path.join(' / ') || u.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Art" required>
              {(id) => (
                <Select id={id} value={teamCategory} onChange={(event) => {
                  const category = event.target.value as TeamCategory;
                  setTeamCategory(category);
                  if (category !== 'YOUTH') setTeamAgeClass(null);
                  if (!divisionGewaehlt) setTeamDivision(category === 'YOUTH' ? 'MIXED' : 'MEN');
                }}>
                  <option value="FIRST">Kampfmannschaft</option>
                  <option value="RESERVE">1b / Reserve</option>
                  <option value="YOUTH">Nachwuchs</option>
                  <option value="OTHER">Weitere Erwachsenenmannschaft</option>
                </Select>
              )}
            </Field>
            <Field label="Damen / Herren" required hint="Für Filter und Übersichten, z. B. Herren-KM und -1b">
              {(id) => (
                <Select id={id} value={teamDivision} onChange={(event) => { setTeamDivision(event.target.value as TeamDivision); setDivisionGewaehlt(true); }}>
                  {TEAM_DIVISION.map((value) => <option key={value} value={value}>{TEAM_DIVISION_LABELS[value]}</option>)}
                </Select>
              )}
            </Field>
            {teamCategory === 'YOUTH' && (
              <Field label="Altersklasse" required>
                {(id) => (
                  <Select id={id} value={teamAgeClass ?? ''} onChange={(event) => setTeamAgeClass(Number(event.target.value))}>
                    <option value="">Auswählen …</option>
                    {Array.from({ length: 14 }, (_, index) => index + 6).map((age) => <option key={age} value={age}>U{age}</option>)}
                  </Select>
                )}
              </Field>
            )}
          </div>
          <p className="text-sm text-slate-500">Die Mannschaft wird nur für die aktuell ausgewählte Saison {selectedSeason?.name ?? ''} angelegt.</p>
        </div>
      </Dialog>

      <Dialog
        open={seasonDialog}
        onClose={() => setSeasonDialog(false)}
        title="Saison anlegen"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSeasonDialog(false)}>
              Abbrechen
            </Button>
            <Button
              loading={createSeason.isPending}
              disabled={!seasonName.trim() || !seasonStart || !seasonEnd}
              onClick={() => createSeason.mutate()}
            >
              Anlegen
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Bezeichnung" required>
            {(id) => (
              <Input
                id={id}
                value={seasonName}
                onChange={(e) => setSeasonName(e.target.value)}
                placeholder="2026/27"
                autoFocus
              />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Beginn" required>
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={seasonStart}
                  onChange={(e) => setSeasonStart(e.target.value)}
                />
              )}
            </Field>
            <Field label="Ende" required>
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={seasonEnd}
                  onChange={(e) => setSeasonEnd(e.target.value)}
                />
              )}
            </Field>
          </div>
          {Boolean(seasons.data?.length) && (
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={copySeniors} onChange={(event) => setCopySeniors(event.target.checked)} />
              <span>Alle Erwachsenenmannschaften aus {seasons.data?.[0]?.name} übernehmen. Dazu zählen Kampfmannschaft, 1b/Reserve und weitere Seniorenteams. Kader werden mitkopiert und können danach angepasst werden.</span>
            </label>
          )}
        </div>
      </Dialog>

      <Dialog
        open={copyDialog}
        onClose={() => setCopyDialog(false)}
        title="Aus Vorsaison übernehmen"
        wide
        footer={<>
          <Button variant="secondary" onClick={() => setCopyDialog(false)}>Abbrechen</Button>
          <Button loading={copyTeams.isPending} disabled={selectedCopyDrafts.length === 0 || selectedCopyDrafts.some((draft) => !draft.name.trim())} onClick={() => copyTeams.mutate()}>Auswahl übernehmen</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label="Vorsaison">
            {(id) => <Select id={id} value={copySourceSeasonId} onChange={(event) => setCopySourceSeasonId(event.target.value)}>
              {previousSeasons.map((season) => <option key={season.id} value={season.id}>{season.name}</option>)}
            </Select>}
          </Field>
          {copyOptions.isLoading && <LoadingBlock />}
          {copyOptions.data?.length === 0 && <EmptyState title="Alles bereits übernommen" description="Aus dieser Vorsaison sind keine weiteren Mannschaften verfügbar." />}
          <ul className="space-y-3">
            {copyOptions.data?.map((option) => {
              const draft = copyDrafts[option.team_id] ?? { selected: false, name: option.suggested_name, copyRoster: true };
              return <li key={option.team_id} className="rounded-xl border border-slate-200 p-3">
                <label className="flex items-center gap-2 font-medium"><input type="checkbox" checked={draft.selected} onChange={(event) => setCopyDrafts({ ...copyDrafts, [option.team_id]: { ...draft, selected: event.target.checked } })} />{option.name} <Badge tone="neutral">{option.player_count} Personen</Badge></label>
                {draft.selected && <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <Field label="Name in neuer Saison">{(id) => <Input id={id} value={draft.name} onChange={(event) => setCopyDrafts({ ...copyDrafts, [option.team_id]: { ...draft, name: event.target.value } })} />}</Field>
                  <label className="flex items-center gap-2 pt-6 text-sm"><input type="checkbox" checked={draft.copyRoster} onChange={(event) => setCopyDrafts({ ...copyDrafts, [option.team_id]: { ...draft, copyRoster: event.target.checked } })} />Kader übernehmen</label>
                </div>}
              </li>;
            })}
          </ul>
        </div>
      </Dialog>

      <Dialog
        open={shiftDialog}
        onClose={() => setShiftDialog(false)}
        title="Helferdienst anlegen"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShiftDialog(false)}>
              Abbrechen
            </Button>
            <Button
              loading={createShift.isPending}
              disabled={!shiftTitle.trim() || (!scope.unitId && !verfuegbareBereiche[0]?.id)}
              onClick={() => createShift.mutate()}
            >
              Anlegen
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Titel" required>
            {(id) => (
              <Input id={id} value={shiftTitle} onChange={(e) => setShiftTitle(e.target.value)} />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Beginn" required>
              {(id) => (
                <Input
                  id={id}
                  type="datetime-local"
                  value={shiftStart}
                  onChange={(e) => {
                    setShiftStart(e.target.value);
                    // Ein Dienst, der endet, wenn er beginnt, wird abgewiesen.
                    if (e.target.value && e.target.value >= shiftEnd) {
                      setShiftEnd(plusMinutes(e.target.value, 120));
                    }
                  }}
                />
              )}
            </Field>
            <Field label="Ende" required>
              {(id) => (
                <Input
                  id={id}
                  type="datetime-local"
                  value={shiftEnd}
                  onChange={(e) => setShiftEnd(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field label="Plätze" required>
            {(id) => (
              <Input
                id={id}
                type="number"
                min="1"
                value={shiftCapacity}
                onChange={(e) => setShiftCapacity(e.target.value)}
              />
            )}
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
